import React, { useEffect, useRef, useState } from "react";

// Claude Design `ds-components/MonthYearPicker.jsx` (CMP Dashboard (11)):
// trigger with a calendar icon, fixed popover with year navigation and a
// 12-month grid. Value format "Nov 2026". Months at or before `minValue`
// are disabled.
const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseMonthYear = (value?: string | null) => {
  const match = String(value || "").trim().match(/^([A-Za-z]{3})[A-Za-z]*\.?\s+(\d{4})$/);
  if (!match) return null;
  const month = monthLabels.findIndex((label) => label.toLowerCase() === match[1].toLowerCase());
  return month < 0 ? null : { month, year: Number(match[2]) };
};

type MonthYearPickerProps = {
  value: string;
  onChange: (value: string) => void;
  minValue?: string;
  placeholder?: string;
  ariaLabel?: string;
};

const MonthYearPicker = ({ value, onChange, minValue, placeholder, ariaLabel }: MonthYearPickerProps) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const current = parseMonthYear(value);
  const min = parseMonthYear(minValue);
  const [year, setYear] = useState(current ? current.year : new Date().getFullYear());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 236;
      const height = 214;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      let top = rect.bottom + 6;
      if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 6);
      setPosition({ top, left });
    };
    place();
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const isDisabled = (month: number) => !!min && year * 12 + month <= min.year * 12 + min.month;

  return (
    <div className="relative min-w-0 flex-[1_1_84px]">
      <button
        ref={triggerRef}
        type="button"
        title={value || ""}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open) setYear(current ? current.year : new Date().getFullYear());
          setOpen(!open);
        }}
        className={`box-border flex w-full min-w-0 cursor-pointer items-center gap-[5px] rounded-[6px] border bg-white px-[6px] py-[3px] text-left text-[12px] ${
          open ? "border-[#E1A731]" : "border-[#9E9E9E]"
        } ${value ? "text-[#0A0A0A]" : "text-[#9E9E9E]"}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#727272" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[12px] w-[12px] shrink-0" aria-hidden="true">
          <rect x={3} y={4} width={18} height={18} rx={2} />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <span className="truncate">{value || placeholder || "Select"}</span>
      </button>
      {open ? (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={ariaLabel ? `${ariaLabel} months` : "Select month"}
          style={{ top: position.top, left: position.left }}
          className="fixed z-[10000] box-border w-[236px] rounded-[10px] border border-[#ECECEC] bg-white p-[12px] shadow-[0_8px_24px_rgba(0,0,0,.14)]"
        >
          <div className="mb-[10px] flex items-center justify-between">
            <button type="button" aria-label="Previous year" onClick={() => setYear(year - 1)} className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border border-[#ECECEC] bg-white p-0 text-[16px] leading-none text-[#0A0A0A]">
              {"‹"}
            </button>
            <span className="text-[14px] font-semibold text-[#0A0A0A]">{year}</span>
            <button type="button" aria-label="Next year" onClick={() => setYear(year + 1)} className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border border-[#ECECEC] bg-white p-0 text-[16px] leading-none text-[#0A0A0A]">
              {"›"}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-[6px]">
            {monthLabels.map((label, month) => {
              const selected = !!current && current.year === year && current.month === month;
              const disabled = isDisabled(month);
              return (
                <button
                  key={label}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onChange(`${label} ${year}`);
                    setOpen(false);
                  }}
                  className={`rounded-[6px] border py-[8px] text-[13px] ${
                    selected
                      ? "border-[#E1A731] bg-[#E1A731] font-semibold text-white"
                      : disabled
                      ? "cursor-not-allowed border-[#ECECEC] bg-white text-[#C4C7CC]"
                      : "border-[#ECECEC] bg-white text-[#0A0A0A]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MonthYearPicker;
